import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Context/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import './TaskManager.css';

const TaskManager = () => {
    const { familyMembers, loading: authLoading } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editingTask, setEditingTask] = useState(null);

    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [priority, setPriority] = useState('medium');
    const [dueDate, setDueDate] = useState('');
    const [assignedMembers, setAssignedMembers] = useState([]);

    const taskCategories = [
        'Garden',
        'House Maintenance',
        'Cleaning',
        'Shopping',
        'Admin',
        'Other'
    ];

    const priorityLevels = [
        { value: 'low', label: 'Low', color: '#28a745' },
        { value: 'medium', label: 'Medium', color: '#ffc107' },
        { value: 'high', label: 'High', color: '#dc3545' }
    ];

    // API functions
    const fetchTasks = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_ENDPOINTS.TASKS}`);
            if (!response.ok) {
                throw new Error('Failed to fetch tasks');
            }
            const data = await response.json();
            // Local server returns array directly, not wrapped in status object
            if (Array.isArray(data)) {
                setTasks(data);
            } else if (data.status === 'Success') {
                setTasks(data.data || []);
            } else {
                throw new Error(data.message || 'Failed to fetch tasks');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error fetching tasks:', err);
        } finally {
            setLoading(false);
        }
    };

    const createTask = async (taskData) => {
        try {
            const response = await fetch(`${API_ENDPOINTS.TASKS}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(taskData)
            });

            if (!response.ok) {
                throw new Error('Failed to create task');
            }

            const data = await response.json();
            // Local server returns task object directly on success
            if (data.id) {
                await fetchTasks(); // Refresh the list
                return true;
            } else if (data.status === 'Success') {
                await fetchTasks(); // Refresh the list
                return true;
            } else {
                throw new Error(data.message || 'Failed to create task');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error creating task:', err);
            return false;
        }
    };

    const updateTask = async (id, taskData) => {
        try {
            const response = await fetch(`${API_ENDPOINTS.TASKS}/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(taskData)
            });

            if (!response.ok) {
                throw new Error('Failed to update task');
            }

            const data = await response.json();
            // Local server returns task object directly on success
            if (data.id) {
                await fetchTasks(); // Refresh the list
                return true;
            } else if (data.status === 'Success') {
                await fetchTasks(); // Refresh the list
                return true;
            } else {
                throw new Error(data.message || 'Failed to update task');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error updating task:', err);
            return false;
        }
    };

    const deleteTask = async (id) => {
        try {
            const response = await fetch(`${API_ENDPOINTS.TASKS}/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('Failed to delete task');
            }

            const data = await response.json();
            // Local server returns message object on successful deletion
            if (data.message && data.deletedTask) {
                await fetchTasks(); // Refresh the list
                return true;
            } else if (data.status === 'Success') {
                await fetchTasks(); // Refresh the list
                return true;
            } else {
                throw new Error(data.message || 'Failed to delete task');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error deleting task:', err);
            return false;
        }
    };

    const toggleTaskCompletion = async (id, completed) => {
        try {
            const response = await fetch(`${API_ENDPOINTS.TASKS}/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    status: completed ? 'completed' : 'pending',
                    completed_date: completed ? new Date().toISOString() : null
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update task');
            }

            const data = await response.json();
            // Local server returns task object directly on success
            if (data.id) {
                await fetchTasks(); // Refresh the list
                return true;
            } else if (data.status === 'Success') {
                await fetchTasks(); // Refresh the list
                return true;
            } else {
                throw new Error(data.message || 'Failed to update task');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error updating task:', err);
            return false;
        }
    };

    // Load tasks on component mount
    useEffect(() => {
        fetchTasks();
    }, []);

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setCategory('');
        setPriority('medium');
        setDueDate('');
        setAssignedMembers([]);
        setEditingTask(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title || !category || !dueDate) {
            setError('Please fill in all required fields');
            return;
        }

        const taskData = {
            title,
            description,
            status: 'pending',
            priority,
            due_date: dueDate,
            assigned_members: assignedMembers,
            family_id: 1,
            tags: [category].filter(Boolean), // Store category as a tag
            created_by: null
        };

        let success;
        if (editingTask) {
            success = await updateTask(editingTask.id, taskData);
        } else {
            success = await createTask(taskData);
        }

        if (success) {
            resetForm();
            setError('');
        }
    };

    const startEditingTask = (task) => {
        setEditingTask(task);
        setTitle(task.title);
        setDescription(task.description || '');
        setCategory(task.tags && task.tags.length > 0 ? task.tags[0] : '');
        setPriority(task.priority || 'medium');
        setDueDate(task.due_date ? task.due_date.split('T')[0] : '');
        setAssignedMembers(task.assigned_members || []);
    };

    const cancelEditing = () => {
        resetForm();
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this task?')) {
            await deleteTask(id);
        }
    };

    const handleToggleCompletion = async (id, completed) => {
        await toggleTaskCompletion(id, !completed);
    };

    const handleMemberToggle = (memberId) => {
        setAssignedMembers(prev =>
            prev.includes(memberId)
                ? prev.filter(id => id !== memberId)
                : [...prev, memberId]
        );
    };

    const getMemberName = (memberId) => {
        const member = familyMembers.find(m => m.id === memberId);
        return member ? member.name : 'Unknown';
    };

    const getMemberColor = (memberId) => {
        const member = familyMembers.find(m => m.id === memberId);
        return member ? member.color : '#ccc';
    };

    const getPriorityColor = (priority) => {
        const priorityObj = priorityLevels.find(p => p.value === priority);
        return priorityObj ? priorityObj.color : '#6c757d';
    };

    const getPriorityLabel = (priority) => {
        const priorityObj = priorityLevels.find(p => p.value === priority);
        return priorityObj ? priorityObj.label : 'Unknown';
    };

    // Filter and sort tasks
    const [filter, setFilter] = useState('open');
    const filteredTasks = tasks.filter(task => {
        switch (filter) {
            case 'open':
            case 'pending':
                return task.status !== 'completed';
            case 'completed':
                return task.status === 'completed';
            case 'overdue':
                return task.status !== 'completed' && new Date(task.due_date) < new Date();
            default:
                return true;
        }
    });

    const sortedTasks = [...filteredTasks].sort((a, b) => {
        // Sort by priority first, then by due date
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];

        if (priorityDiff !== 0) return priorityDiff;

        return new Date(a.due_date) - new Date(b.due_date);
    });

    if (loading || authLoading) {
        return <div className="task-manager-container">Loading tasks...</div>;
    }

    return (
        <div className="task-manager-container">
            <h2>Task Manager</h2>

            {error && <div className="error-message">{error}</div>}

            {/* Tasks Section - Moved to top */}
            <div className="tasks-section">
                <div className="tasks-header">
                    <h3>Tasks</h3>
                    <div className="header-actions">
                        <button
                            type="button"
                            onClick={() => document.getElementById('task-form-section').scrollIntoView({ behavior: 'smooth' })}
                            className="btn btn-primary add-task-btn"
                        >
                            ➕ Add New Task
                        </button>
                        <div className="filter-controls">
                            <select
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="form-control"
                            >
                                <option value="all">All Tasks</option>
                                <option value="open">Open Tasks</option>
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                                <option value="overdue">Overdue</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="tasks-grid">
                    {sortedTasks.length === 0 ? (
                        <p className="no-tasks">No tasks found</p>
                    ) : (
                        sortedTasks.map(task => (
                            <div key={task.id} className={`task-card ${task.status === 'completed' ? 'completed' : ''}`}>
                                <div className="task-header">
                                    <div className="task-priority">
                                        <span
                                            className="priority-badge"
                                            style={{ backgroundColor: getPriorityColor(task.priority) }}
                                        >
                                            {getPriorityLabel(task.priority)}
                                        </span>
                                    </div>
                                    <div className="task-actions">
                                        <button
                                            onClick={() => startEditingTask(task)}
                                            className="btn btn-sm btn-outline-primary"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            onClick={() => handleDelete(task.id)}
                                            className="btn btn-sm btn-outline-danger"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>

                                <div className="task-content">
                                    <h4 className="task-title">{task.title}</h4>
                                    {task.description && (
                                        <p className="task-description">{task.description}</p>
                                    )}

                                    <div className="task-details">
                                        <div className="detail-row">
                                            <span className="detail-label">Category:</span>
                                            <span className="detail-value">{task.tags && task.tags.length > 0 ? task.tags[0] : 'None'}</span>
                                        </div>
                                        <div className="detail-row">
                                            <span className="detail-label">Due Date:</span>
                                            <span className={`detail-value ${new Date(task.due_date) < new Date() && task.status !== 'completed' ? 'overdue' : ''}`}>
                                                {new Date(task.due_date).toLocaleDateString('en-GB')}
                                            </span>
                                        </div>
                                        {task.assigned_members && task.assigned_members.length > 0 && (
                                            <div className="detail-row">
                                                <span className="detail-label">Assigned to:</span>
                                                <div className="assigned-members">
                                                    {task.assigned_members.map(memberId => (
                                                        <span
                                                            key={memberId}
                                                            className="member-badge"
                                                            style={{ backgroundColor: getMemberColor(memberId) }}
                                                        >
                                                            {getMemberName(memberId)}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="task-footer">
                                    <button
                                        onClick={() => handleToggleCompletion(task.id, task.status === 'completed')}
                                        className={`btn btn-sm ${task.status === 'completed' ? 'btn-success' : 'btn-outline-success'}`}
                                    >
                                        {task.status === 'completed' ? '✓ Completed' : 'Mark Complete'}
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Form Section - Moved to bottom */}
            <div id="task-form-section" className="form-section">
                <h3>{editingTask ? 'Edit Task' : 'Add New Task'}</h3>
                <form onSubmit={handleSubmit} className="task-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="title">Title *</label>
                            <input
                                type="text"
                                id="title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="form-control"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="category">Category *</label>
                            <select
                                id="category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="form-control"
                                required
                            >
                                <option value="">Select Category</option>
                                {taskCategories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="priority">Priority</label>
                            <select
                                id="priority"
                                value={priority}
                                onChange={(e) => setPriority(e.target.value)}
                                className="form-control"
                            >
                                {priorityLevels.map(level => (
                                    <option key={level.value} value={level.value}>
                                        {level.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="dueDate">Due Date *</label>
                            <input
                                type="date"
                                id="dueDate"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                className="form-control"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="form-control"
                            rows="3"
                        />
                    </div>

                    <div className="form-group">
                        <label>Assign to Family Members</label>
                        <div className="member-selection">
                            {familyMembers.map(member => (
                                <label key={member.id} className="member-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={assignedMembers.includes(member.id)}
                                        onChange={() => handleMemberToggle(member.id)}
                                    />
                                    <span
                                        className="member-name"
                                        style={{ color: member.color }}
                                    >
                                        {member.name}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-actions">
                        {editingTask ? (
                            <>
                                <button type="submit" className="btn btn-primary">
                                    Update Task
                                </button>
                                <button type="button" onClick={cancelEditing} className="btn btn-secondary">
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button type="submit" className="btn btn-primary">
                                Add Task
                            </button>
                        )}
                    </div>
                </form>
            </div>


        </div>
    );
};

export default TaskManager;
